import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography,
  Autocomplete,
} from "@mui/material";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  useAddStudentToClassroomMutation,
  useCreateStudentMutation,
} from "./studentApiSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import toast from "../../../helpers/toast";
import { useCreateUserMutation } from "../../../app/authApiSlice";
import { useSelector } from "react-redux";
import React from "react";
import { selectCurrentUser } from "../auth/authSlice";
import getErrorMessage from "../../../utils/getErrorMessage";

const validationSchema = yup.object({
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  gender: yup.string().required("Required"),
  dob: yup.string(),
  classroom: yup.string().required("Required"),
});

function AddStudentForm() {
  const user = useSelector(selectCurrentUser);
  const school_id = user.current_school_id;

  const [classroomSearch, setClassroomSearch] = React.useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({
    page: 1,
    per_page: 10,
    q: classroomSearch,
  });
  const classrooms = classroomsData?.classrooms ?? [];
  const [createStudent, { isLoading: createStudentLoading }] =
    useCreateStudentMutation();
  const [uploadFile, { isLoading: fileLoading }] = useUploadFileMutation();

  const [createUser, { isLoading: createUserLoading }] =
    useCreateUserMutation();
  const [addStudentToClass] = useAddStudentToClassroomMutation();

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      dob: "",
      gender: "",
      classroom: "",
      address: "",
      phone: "",
      photo: "",
      reg_number: "",
      parent_email: "",
      parent_email2: "",
      height: "",
      weight: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const createUserBody = {
          ...values,
          school_id,
          username: values.email,
          roles: ["student"],
        };

        const userResponse = await createUser(createUserBody).unwrap();
        const user_id = userResponse.user.id;

        const createStudentBody = {
          ...values,
          user_id,
          school_id,
        };

        const response = await createStudent(createStudentBody).unwrap();
        if (response.id) {
          toast.success("Student created successfully");
          const res = await addStudentToClass({
            body: {
              student_ids: [response.id],
            },
            classroom_id: values.classroom,
          }).unwrap();
          if (res) {
            toast.success("Student added to classroom");
            formik.resetForm();
          }
        }
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
  });

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      if (response) {
        formik.setFieldValue(e.target.name, response.url);
      }
    } catch (error) {}
  };

  return (
    <StyledPaper>
      <Box className="paperHeader">
        <Typography variant="formHeading">
          New Student Registration Form
        </Typography>
      </Box>
      <Box className="paperBody">
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={3}>
            <Grid item xs={12} lg={4}>
              <FormControl
                fullWidth
                error={formik.touched.first_name && formik.errors.first_name}
              >
                <Typography variant="formSubHeading">first name</Typography>
                <StyledTextField
                  bold
                  variant="outlined"
                  id="first_name"
                  name="first_name"
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.first_name &&
                    Boolean(formik.errors.first_name)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.first_name && formik.errors.first_name}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} lg={4}>
              <FormControl
                fullWidth
                error={
                  formik.touched.last_name && Boolean(formik.errors.last_name)
                }
              >
                <Typography variant="formSubHeading">last name</Typography>
                <StyledTextField
                  bold
                  variant="outlined"
                  id="last_name"
                  name="last_name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.last_name && Boolean(formik.errors.last_name)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.last_name && formik.errors.last_name}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} lg={4}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">
                  registration number
                </Typography>
                <StyledTextField
                  variant="outlined"
                  id="reg_number"
                  name="reg_number"
                  value={formik.values.reg_number}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.reg_number &&
                    Boolean(formik.errors.reg_number)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.reg_number && formik.errors.reg_number}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* end of first row */}

            {/* beggining second row inputs */}

            <Grid item xs={12} md={4}>
              <FormControl
                error={formik.touched.dob && formik.errors.dob}
                fullWidth
              >
                <Typography htmlFor="section" variant="formSubHeading">
                  Date of birth
                </Typography>
                <StyledTextField
                  type="date"
                  id="dob"
                  name="dob"
                  value={formik.values.dob}
                  onChange={formik.handleChange}
                  error={formik.touched.dob && Boolean(formik.errors.dob)}
                />

                <FormHelperText variant="error">
                  {formik.touched.dob && formik.errors.dob}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl
                error={formik.touched.gender && formik.errors.gender}
                fullWidth
              >
                <Typography htmlFor="gender" variant="formSubHeading">
                  gender
                </Typography>
                <StyledSelect
                  displayEmpty
                  id="gender"
                  name="gender"
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                  error={formik.touched.gender && Boolean(formik.errors.gender)}
                >
                  <MenuItem value="" disabled>
                    <em>select gender</em>
                  </MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </StyledSelect>
                <FormHelperText variant="error">
                  {formik.touched.gender && formik.errors.gender}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">photo</Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <CustomFileInput
                    id="photo"
                    name="photo"
                    accept="image/*"
                    placeholder="Choose photo..."
                    value={formik.values.photo}
                    onChange={(e) => handleFileChange(e)}
                    error={formik.touched.photo && Boolean(formik.errors.photo)}
                  />
                  {fileLoading && <CircularProgress size={20} />}
                </Stack>

                <FormHelperText variant="error">
                  {formik.touched.photo && formik.errors.photo}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* beginning of third row inputs */}

            <Grid item xs={12} md={4}>
              <FormControl
                error={formik.touched.classroom && formik.errors.classroom}
                fullWidth
              >
                <Typography htmlFor="classroom" variant="formSubHeading">
                  classroom
                </Typography>
                <Autocomplete
                  options={classrooms}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={
                    classrooms.find((c) => c.id === formik.values.classroom) ||
                    null
                  }
                  onChange={(_, value) =>
                    formik.setFieldValue("classroom", value?.id || "")
                  }
                  onInputChange={(_, value, reason) => {
                    if (reason === "input") setClassroomSearch(value);
                  }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search classroom..."
                      error={
                        formik.touched.classroom &&
                        Boolean(formik.errors.classroom)
                      }
                    />
                  )}
                />
                <FormHelperText variant="error">
                  {formik.touched.classroom && formik.errors.classroom}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">height (cm)</Typography>
                <StyledTextField
                  height="40px"
                  type="number"
                  id="height"
                  name="height"
                  placeholder="e.g. 145"
                  value={formik.values.height}
                  onChange={formik.handleChange}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">weight (kg)</Typography>
                <StyledTextField
                  height="40px"
                  type="number"
                  id="weight"
                  name="weight"
                  placeholder="e.g. 40"
                  value={formik.values.weight}
                  onChange={formik.handleChange}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={12}>
              <FormControl error fullWidth>
                <Typography variant="formSubHeading">address</Typography>
                <StyledTextField
                  variant="outlined"
                  id="address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                />
                <FormHelperText variant="error">
                  {formik.touched.address && formik.errors.address}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* end of third row inputs */}

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  Parent 1 Email (e.g. Mother)
                </Typography>
                <StyledTextField
                  variant="outlined"
                  id="parent_email"
                  name="parent_email"
                  placeholder="e.g. mother@email.com"
                  value={formik.values.parent_email}
                  onChange={formik.handleChange}
                />
                <FormHelperText>
                  Links this student to Parent 1 for parent portal access.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">
                  Parent 2 Email (e.g. Father)
                </Typography>
                <StyledTextField
                  variant="outlined"
                  id="parent_email2"
                  name="parent_email2"
                  placeholder="e.g. father@email.com"
                  value={formik.values.parent_email2}
                  onChange={formik.handleChange}
                />
                <FormHelperText>
                  Links this student to Parent 2 for parent portal access.
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={12} textAlign={"right"}>
              <StyledLoadingButton
                loading={createStudentLoading || createUserLoading}
                type="submit"
              >
                SUBMIT
              </StyledLoadingButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </StyledPaper>
  );
}

export default AddStudentForm;
